import { AppointmentContainerBase } from "@/components/layout/AppointmentContainer";
import { useCalendarContext } from "@/features/calendar/CalendarProvider";
import { isUnassignedLane } from "@/features/calendar/constants";
import { type OpenModalProps } from "@/features/calendar/openCalendarModal";
import {
  type CoWorkerCalendarItem,
  isCoWorkerCalendarBokaAppointment,
  isCoWorkerCalendarTask,
  isCoWorkerScheduleActivity,
} from "@/features/calendar/types";
import {
  getBlockLength,
  getColor,
  getIsDashed,
  getRenderTypeInfoFromCalendarItem,
  isHiddenActivity,
} from "@/features/calendar/utils";
import { isWorking } from "@/features/calendar/work-schedule/getWorkHours";
import styled from "@emotion/styled";
import { useTeam } from "@repo/api";
import { LightMode, Variables } from "@repo/skapa";
import {
  type ActivityConfig,
  CoWorkerName,
  type TaskConfig,
  mapStores,
  useSettings,
  useStores,
} from "@repo/ui";
import { differenceInMinutes, max, min } from "date-fns";
import { type ReactNode } from "react";

import { type CalenderAppointmentsSearchFilters } from "../calendarFilter/appointmentFilterReducer";
import { selectTeam } from "../calendarFilter/appointmentFilterSelector";
import { DraggableAppointmentBlockContainer } from "../components/DraggableAppointmentBlockContainer";
import {
  getHeightOfRowItems,
  getLaneHeight,
  getZoomHourWidth,
} from "../utils/utils";
import { CoWorkerBlock } from "./CoWorkerBlock";
import { DroppableHourBlock } from "./DroppableHourBlock";
import { TeamCalendarAppointment } from "./TeamCalendarAppointment";
import { CalendarRow } from "./components";
import { BLOCKS_PER_HOUR, NAME_COL_WIDTH } from "./constants";
import { type CoWorkerCalendar } from "./types";

const AppointmentBlock = styled(AppointmentContainerBase)`
  ${({ dashed, borderColor }) =>
    dashed ? "padding-top: 5px;" : `border-top: 3px solid ${borderColor};`}

  padding: 0 2px;
  ${({ dashed }) => (dashed ? "padding-top: 3px;" : "")}

  border-radius: ${Variables.radiusS};
  ${({ dashed, borderColor }) =>
    dashed
      ? `
        background-size: 100% 3px, 100% 1px, 1px 100%, 100% 1px;
        background-repeat: no-repeat;
        background-image: repeating-linear-gradient(
          135deg,
          ${borderColor},
          ${borderColor} 3px,
          transparent 3px,
          transparent 6px
      );`
      : ""}

  cursor: pointer;
`;

function renderTasks(
  item: CoWorkerCalendarItem,
  startHour: number,
  endHour: number,
  selectedDate: Date,
  calBlockWidth: number,
  top: number,
  openModal: ((props: OpenModalProps) => void) | undefined,
  isItemSelected: (id: string) => boolean,
  taskConfigs?: TaskConfig[],
  activityConfigs?: ActivityConfig[]
) {
  const calendarStart = new Date(selectedDate);
  calendarStart.setHours(startHour, 0, 0, 0);
  const calendarEnd = new Date(calendarStart);
  calendarEnd.setHours(endHour, 0, 0, 0);

  const visibleStart = max([item.start, calendarStart]);
  const visibleEnd = min([item.end, calendarEnd]);
  const visibleLength =
    visibleStart < visibleEnd
      ? differenceInMinutes(visibleEnd, visibleStart)
      : 0;
  const width = getBlockLength(
    visibleLength,
    calBlockWidth,
    endHour - startHour
  );
  const frameLeft = NAME_COL_WIDTH + 1;
  const frameWidth = (endHour - startHour) * calBlockWidth;
  const rawLeft =
    frameLeft +
    (differenceInMinutes(visibleStart, calendarStart) * calBlockWidth) / 60;
  const left = Math.max(
    frameLeft,
    Math.min(rawLeft, frameLeft + frameWidth - width)
  );

  const renderinfo = getRenderTypeInfoFromCalendarItem(item);
  const colorInfo = getColor(renderinfo, taskConfigs, activityConfigs);

  return (
    <DraggableAppointmentBlockContainer
      key={item.id}
      id={item.id}
      item={item}
      top={top}
      left={left}
      width={width}
      onClick={() => {
        if (isCoWorkerCalendarTask(item)) {
          openModal?.({ type: "task", id: item.id });
        } else if (isCoWorkerCalendarBokaAppointment(item)) {
          openModal?.({
            type: "appointment",
            opportunityId: item.opportunityId,
            appointmentId: Number(item.id),
            dropInId: Number(item.id),
            id: item.id,
          });
        } else if (isCoWorkerScheduleActivity(item)) {
          openModal?.({ type: "scheduled_activity", item });
        }
      }}
    >
      <AppointmentBlock
        backgroundColor={colorInfo.color}
        borderColor={colorInfo.borderColor}
        dashed={getIsDashed(renderinfo)}
        selected={isItemSelected(item.id)}
      >
        <TeamCalendarAppointment item={item} />
      </AppointmentBlock>
    </DraggableAppointmentBlockContainer>
  );
}

export const CoWorkers = ({
  startHour,
  totalHours,
  coWorkers,
  filters,
  openModal,
}: {
  startHour: number;
  totalHours: number;
  coWorkers: CoWorkerCalendar[];
  filters: CalenderAppointmentsSearchFilters;
  openModal?: (props: OpenModalProps) => void;
}) => {
  const { isItemSelected } = useCalendarContext();
  const { data: team } = useTeam(selectTeam(filters));
  const { data: stores } = useStores();
  const storesData = mapStores(stores);
  const calBlockWidth = getZoomHourWidth(filters.calendarSettings, totalHours);
  const { task_configuration, calendar } = useSettings();
  const res = [];
  let cwCount = 0;
  for (const cw of coWorkers) {
    const isLastLane = cwCount === coWorkers.length - 1;
    const hourBlocks = [];
    const isUnassigned = isUnassignedLane(cw.emailId);

    for (let i = 0; i < totalHours * BLOCKS_PER_HOUR; i++) {
      const date = new Date(filters.selectedDate);
      date.setHours(startHour + i / BLOCKS_PER_HOUR);
      date.setMinutes((i % BLOCKS_PER_HOUR) * (60 / BLOCKS_PER_HOUR));

      hourBlocks.push(
        <DroppableHourBlock
          id={`${cw.emailId}-${i.toString()}`}
          emailId={cw.emailId}
          selectedTeam={selectTeam(filters)}
          date={date}
          key={i}
          noLeftBorder={(i + 1) % BLOCKS_PER_HOUR !== 1}
          first={i === 0}
          last={isLastLane}
          width={calBlockWidth / BLOCKS_PER_HOUR}
          isNotWorking={isUnassigned ? false : !isWorking(cw.workHours, date)}
          color={
            isWorking(cw.workHours, date) || isUnassigned
              ? LightMode.colourStaticWhite
              : LightMode.colourStaticLightGrey
          }
          onClick={
            openModal
              ? () => {
                  openModal({
                    type: "newTask",
                    initialData: {
                      startTime: date,
                      selectedCoworkers: isUnassigned ? undefined : cw.emailId,
                      selectedTeam: isUnassigned
                        ? selectTeam(filters)
                        : undefined,
                      cmpCode: team?.buCode,
                      cmpName: team?.buCode
                        ? storesData?.[team?.buCode]
                        : undefined,
                      hfb: team?.hfbs?.[0],
                    },
                  });
                }
              : undefined
          }
        />
      );
    }

    const meetingBlocks: ReactNode[] = [];
    let top = 0;

    const visibleMeetings = cw.meetings.map((row) =>
      row.filter(
        (item) => !isHiddenActivity(item, calendar?.hide_absence_blocks)
      )
    );

    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let k = 0; k < visibleMeetings.length; k++) {
      for (const item of visibleMeetings[k]) {
        meetingBlocks.push(
          renderTasks(
            item,
            startHour,
            startHour + totalHours,
            filters.selectedDate,
            calBlockWidth,
            top,
            openModal,
            isItemSelected,
            task_configuration?.task_types,
            calendar?.activity_configuration
          )
        );
      }
      // Set top pos of next row
      top += getHeightOfRowItems(visibleMeetings[k]);
    }

    res.push(
      <CalendarRow key={`CoWorkerLane-${cwCount++}`}>
        <CoWorkerBlock
          last={isLastLane}
          heightInPx={getLaneHeight(visibleMeetings)}
          coWorker={cw}
        >
          {!isUnassigned && (
            <div>
              <CoWorkerName
                email={cw.emailId}
                emails={coWorkers.map((c) => c.emailId)}
                textStyle="Body.S"
                bold
              />
            </div>
          )}
        </CoWorkerBlock>
        {hourBlocks.map((b) => b)}
        {meetingBlocks.map((b) => b)}
      </CalendarRow>
    );
  }

  return <>{res}</>;
};
